import { createTheme, ThemeOptions } from '@material-ui/core';
import React from 'react';

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    mint: Palette['primary'];
    marble: Palette['primary'];
    pearl: Palette['primary'];

    lavender: Palette['secondary'];
    indigo: Palette['secondary'];
    summer: Palette['secondary'];
    salmon: Palette['secondary'];
    black: Palette['secondary']

    bg1: Palette['background'];
    bg2: Palette['background'];
    bg3: Palette['background'];
    bg4: Palette['background'];

    text1: Palette['info'];
    text2: Palette['info'];
    text3: Palette['info'];
    text4: Palette['info'];
    text5: Palette['info'];
    text6: Palette['info'];
    text7: Palette['info'];
  }
  interface PaletteOptions {
    mint: PaletteOptions['primary'];
    marble: PaletteOptions['primary'];
    pearl: PaletteOptions['primary'];

    lavender: PaletteOptions['secondary'];
    indigo: PaletteOptions['secondary'];
    summer: PaletteOptions['secondary'];
    salmon: PaletteOptions['secondary'];
    black: PaletteOptions['secondary']

    bg1: PaletteOptions['background'];
    bg2: PaletteOptions['background'];
    bg3: PaletteOptions['background'];
    bg4: PaletteOptions['background'];

    text1: PaletteOptions['info'];
    text2: PaletteOptions['info'];
    text3: PaletteOptions['info'];
    text4: PaletteOptions['info'];
    text5: PaletteOptions['info'];
    text6: PaletteOptions['info'];
    text7: PaletteOptions['info'];
  }
}

declare module '@material-ui/core/styles/createTypography' {
  interface Typography {
    display: React.CSSProperties;
    small: React.CSSProperties;
    xsmall: React.CSSProperties;
    capitalize: React.CSSProperties;
  }

  interface TypographyOptions {
    display?: React.CSSProperties;
    small?: React.CSSProperties;
    xsmall?: React.CSSProperties;
    capitalize?: React.CSSProperties;
  }
}

declare module '@material-ui/core/Typography/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
    small: true;
    xsmall: true;
    capitalize: true;
  }
}

const themeOptions: ThemeOptions = {
  // overrides: {
  //   MuiButton: {
  //     root: {
  //       textTransform: 'none',
  //       lineHeight: 1.2,
  //       minHeight: 36,
  //     },
  //     outlined: {
  //       borderWidth: '2px',
  //       '&:hover': {
  //         borderWidth: '2px',
  //       },
  //     },
  //     outlinedPrimary: {
  //       borderWidth: '2px',
  //       '&:hover': {
  //         borderWidth: '2px',
  //       },
  //     },
  //     outlinedSecondary: {
  //       borderWidth: '2px',
  //       '&:hover': {
  //         borderWidth: '2px',
  //       },
  //     }
  //   },
  // },
  palette: {
    primary: {
      light: '#ECFDF8',
      main: '#12D896',
      dark: '#10BE84',
    },
    secondary: {
      main: '#507FEE',
      light: '#7597E8',
    },
    // Primary colors
    mint: {
      light: '#ECFDF8',
      main: '#12D896',
      dark: '#10BE84',
    },
    marble: {
      main: '#F9F8F7',
    },
    pearl: {
      main: '#FEFEFE',
    },
    // Secondary colors
    lavender: {
      main: '#507FEE',
      light: '#7597E8',
    },
    indigo: {
      main: '#23375E',
    },
    summer: {
      main: '#F9CF40',
    },
    salmon: {
      main: '#FF7A75',
    },
    black: {
      main: '#1C2026',
    },
    // Backgrounds
    bg1: {
      default: '#D8D8D8',
    },
    bg2: {
      default: '#E5E5E5',
    },
    bg3: {
      default: '#F2F1F0',
    },
    bg4: {
      default: '#F9F8F7',
    },
    // Text
    text1: { main: '#1C2026' },
    text2: { main: '#383C44' },
    text3: { main: '#5F6674' },
    text4: { main: '#878F9E' },
    text5: { main: '#B4BAC5' },
    text6: { main: '#C3C8D1' },
    text7: { main: '#D1D5DC' },
  },
  typography: {
    fontWeightLight: 300,
    fontWeightBold: 600,
    fontWeightRegular: 400,
    fontFamily: '"Poppins", sans-serif',
    allVariants: {
      color: '#1C2026',
    },
    display: {
      fontSize: '3.875rem',
      fontWeight: 300,
      color: '#1C2026',
      letterSpacing: '-0.04em',
    },
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.04em',
      color: '#1C2026',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      letterSpacing: '-0.04em',
      color: '#1C2026',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.04em',
      color: '#1C2026',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 300,
      letterSpacing: '-0.04em',
      color: '#1C2026',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 300,
      letterSpacing: '-0.04em',
      color: '#1C2026',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 300,
      color: '#1C2026',
      letterSpacing: '-0f.04em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 300,
      color: '#1C2026',
      letterSpacing: 0,
      lineHeight: 1.2,
    },
    small: {
      fontSize: '0.75rem',
      fontWeight: 300,
      color: '#1C2026',
      letterSpacing: 0,
    },
    xsmall: {
      fontSize: '0.625rem',
      fontWeight: 300,
      color: '#1C2026',
      letterSpacing: 0,
    },
    capitalize: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#1C2026',
      letterSpacing: '1px',
      textTransform: 'capitalize',
    },
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.1)',
    '0px 1px 4px rgba(0, 0, 0, 0.1)',
    '0px 2px 8px rgba(0, 0, 0, 0.1)',
    '0px 3px 12px rgba(0, 0, 0, 0.12)',
    '0px 10px 16px rgba(0, 0, 0, 0.1)',
    '0px 14px 20px rgba(0, 0, 0, 0.1)',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
};

const theme = createTheme(themeOptions);
export type TranquiTheme = typeof theme;
/*
  when this theme is going to be used in "useStyles" it's necessary to call it
  For example
  const useStyles = makeStyles((theme: TranquiTheme) => {
    ...
  })
*/
export default theme;
